import { BaseScreen } from "../../../screens/BaseScreen";
import { CharacterCard } from "../../../ui/CharacterCard";
import { Button } from "../../../ui/Button";
import { LAYOUT, TEXT_STYLES } from "../../../constants";
import { CharacterFactory, type CharacterId } from "../../../characters";
import { PLAYER_BLUEPRINT } from "../../../avatars/blueprints/player";
import { BARBARIAN_BLUEPRINT } from "../../../avatars/blueprints/barbarian";
import { MainMenuScene } from "../../MainMenuScene";
import type { ScreenContext } from "../../../screens/types";
import { tagTextForAutomation } from "../../../automation/automationTags";
import { setSelectedCharacter, getSelectedCharacter } from "../../../state/characterPersistence";

export class CharacterSelectScreen extends BaseScreen<MainMenuScene> {
  private selectedCharacterId?: CharacterId;
  private characterCards: Map<string, CharacterCard> = new Map();

  constructor(scene: MainMenuScene) {
    super(scene, "CHARACTER_SELECT");
  }

  enter(_context?: ScreenContext) {
    const { width, height } = this.scene.scale;

    // Start with container invisible for fade-in
    this.container.setAlpha(0);

    // Title
    const title = this.scene.add
      .text(width / 2, LAYOUT.screenPadding + 20, "Choose Your Hero", TEXT_STYLES.title)
      .setOrigin(0.5, 0);
    this.addToContainer(title);
    tagTextForAutomation(title, "character-select-title");

    // Get all available characters
    const characters = CharacterFactory.getAll();

    // Card dimensions
    const cardWidth = width - LAYOUT.screenPadding * 2;
    const cardHeight = 160;
    const startY = 140;

    // Create character cards
    characters.forEach((character, index) => {
      const cardY = startY + index * (cardHeight + LAYOUT.buttonSpacing);

      // Get starting skill names
      const skillNames = Object.keys(character.startingSkills);

      // Get sprite blueprint based on character
      let sprite;
      if (character.sprite === "player") {
        sprite = PLAYER_BLUEPRINT;
      } else if (character.sprite === "barbarian") {
        sprite = BARBARIAN_BLUEPRINT;
      }

      const card = new CharacterCard(this.scene, LAYOUT.screenPadding, cardY, {
        width: cardWidth,
        height: cardHeight,
        characterName: character.name,
        description: character.description,
        stats: character.stats,
        startingSkills: skillNames,
        sprite,
        automationName: `character-card-${character.id}`,
        onClick: () => this.selectCharacter(character.id),
      });

      this.addToContainer(card);
      this.characterCards.set(character.id, card);
    });

    // Bottom button
    const buttonWidth = width - LAYOUT.screenPadding * 2;
    const buttonY = height - LAYOUT.screenPadding - LAYOUT.buttonHeight;

    // Back button (full width)
    const backButton = new Button(this.scene, LAYOUT.screenPadding, buttonY, {
      width: buttonWidth,
      height: LAYOUT.buttonHeight,
      label: "Back",
      automationName: "back-to-menu",
      onClick: () => this.goBack(),
    });
    this.addToContainer(backButton);

    // Cleanup
    this.trackCleanup(() => {
      this.characterCards.clear();
    });

    // Auto-select currently saved character for visual feedback only
    const currentSelection = getSelectedCharacter();
    if (currentSelection && this.characterCards.has(currentSelection)) {
      this.setSelectedVisual(currentSelection);
    } else if (characters.length > 0) {
      // Fallback to first character if saved selection doesn't exist
      this.setSelectedVisual(characters[0].id);
    }

    // Fade in the entire screen smoothly
    this.addTween({
      targets: this.container,
      alpha: 1,
      duration: 300,
      ease: "Sine.easeOut",
    });
  }

  private setSelectedVisual(characterId: CharacterId) {
    // Just update visual state without saving or navigating
    this.characterCards.forEach((card) => card.setSelected(false));
    const selectedCard = this.characterCards.get(characterId);
    if (selectedCard) {
      selectedCard.setSelected(true);
      this.selectedCharacterId = characterId;
    }
  }

  private selectCharacter(characterId: CharacterId) {
    // Update visual selection
    this.setSelectedVisual(characterId);

    // Save selection to persistence
    setSelectedCharacter(characterId);

    // Auto-return to start screen to show the new selection
    this.scene.showStartScreen();
  }

  private goBack() {
    // Return to start screen without changing selection
    this.scene.showStartScreen();
  }
}
